import { z } from 'zod'
import { ApiError } from './client'
import type { ApiClient } from './client'
import { loginResponse } from './auth'

/**
 * Session handoff — carrying a signed-in user across the origin boundary
 * between this console and the legacy Bloo Console, without a second login.
 *
 * Spec: `docs/session-handoff.md`, from lc-api's "Session Handoff Integration"
 * brief (branch `ui-handoff`, revised 2026-09-17). The two deployments share
 * one MongoDB and one Redis, so the user's SSID is *already* a valid session on
 * both domains. Nothing is re-authenticated. The only obstacle is the browser:
 * the SSID lives in storage scoped to the origin that created it, so the peer's
 * JavaScript cannot read it. The token exists to CARRY the session across that
 * boundary — a transport mechanism, not a trust mechanism.
 *
 * That distinction is the whole error model here: a failed handoff never means
 * "this user is not allowed". It means the ticket was stale, already spent, or
 * the underlying session timed out.
 *
 * Both halves live here. `/start` mints (this console is the source when the
 * user picks "Bloo Console (legacy)"); `/redeem` spends (this console is the
 * destination when the legacy console's own switch sends a user here). The peer
 * map on the backend is a symmetric pair — `ENV_PEERS` in `utils/constants.py`
 * maps each host to the other — so both directions are real traffic and both
 * ends of the wire belong in one module.
 */

/**
 * The success field is `redirect_url`, NOT `url`.
 *
 * Same spelling as `/lc/users/saml/login` (see `samlResponse` in ./auth) —
 * `/start` is copy-pasted from the SAML flow, which is also why its success
 * `message` reads "SAML initialization success" and means nothing. Never branch
 * on that message: branch on the status code and on this field's presence, as
 * the brief's Quirks section instructs.
 */
export const handoffStartResponse = z.object({
  data: z
    .object({
      redirect_url: z.string().min(1),
    })
    .passthrough(),
})

/**
 * `/redeem` answers with the `/login` body byte for byte — guarded on the
 * backend by `tests/test_login_payload_parity.py`, which fails if either side
 * gains or loses a field. Reusing the login schema is the point: the parity
 * test is what keeps this line honest.
 */
export const handoffRedeemResponse = loginResponse

export interface HandoffTarget {
  /**
   * The peer URL to navigate to, carrying a single-use token in its path.
   * Named `url` here because that is what it is to a caller; the wire spells
   * it `redirect_url` (above).
   */
  url: string
}

export interface HandoffSession {
  /** The SAME session id the source tab holds — by design, so it stays signed in. */
  ssid: string
}

/**
 * Why the failure happened, in the only shapes a caller can act on:
 *
 *   no-peer  — this deployment has no counterpart configured. Not an error the
 *              user can fix or should ever see: stop offering the control.
 *   session  — the source session is missing or expired. Send them to login.
 *   link     — the ticket is unusable: absent, older than 60s, already spent,
 *              or not signed for this origin. Redeem-side only. Say the link
 *              expired and offer sign-in — never "something went wrong".
 *   server   — anything else. Say so and leave them where they are.
 */
export type HandoffFailure = 'no-peer' | 'session' | 'link' | 'server'

export class HandoffError extends Error {
  readonly reason: HandoffFailure
  readonly status: number | null
  constructor(reason: HandoffFailure, message: string, status: number | null = null) {
    super(message)
    this.name = 'HandoffError'
    this.reason = reason
    this.status = status
  }
}

/**
 * The peer-not-configured refusal is a 400 whose MESSAGE is the entire signal:
 * `{"status":"error","message":"peer environment not configured"}`. Matched
 * loosely (case-insensitive, on the stable part of the phrase) because the
 * console must not start showing a permanently broken menu item over a
 * backend copy edit.
 *
 * This is the one place we knowingly read a message string against the brief's
 * "branch on the status code" advice, and it is unavoidable: both documented
 * `/start` refusals are 400s, so the message is the only thing telling them
 * apart. The fallback is safe — an unrecognised 400 stays `server`, which
 * keeps the item on offer rather than hiding it forever.
 */
function isNoPeer(message: string | undefined): boolean {
  return message !== undefined && /peer environment/i.test(message)
}

/**
 * A dead session reaches `/start` two ways: as HTTP 401, or — the legacy
 * backend's habit — as a 400 carrying `{"status":"invalid","message":"invalid"}`.
 * That 400 is systemic, not specific to handoff: `@session_valid` answers every
 * endpoint that way (`helpers/api_helper.py:59`), so 401 alone can never detect
 * a dead session here.
 *
 * Anything else that is merely a 400 stays 'server': an unrecognised refusal
 * must not sign the user out of a session that is working fine.
 */
function classifyStart(e: unknown): HandoffFailure {
  if (!(e instanceof ApiError)) return 'server'
  if (isNoPeer(e.serverMessage)) return 'no-peer'
  if (e.status === 401 || e.status === 419) return 'session'
  if (e.status === 400) {
    const body = e.body as { status?: unknown } | null
    const envelopeStatus = typeof body === 'object' && body !== null ? body.status : undefined
    if (envelopeStatus === 'invalid' || e.serverMessage === 'invalid') return 'session'
  }
  return 'server'
}

/**
 * `/redeem`'s failures are genuine 401s — all three of them
 * (`handoff link expired`, `invalid handoff token`, `session expired`) mean the
 * same thing to a user standing on a dead link, so they collapse to 'link'.
 * The one 400, `token is required`, is a route matched with an empty param:
 * also a broken link.
 *
 * `status: 'invalid'` inside a 200 arrives as an ApiError(401) from the shared
 * client's envelope check, so it lands here too — correctly.
 */
function classifyRedeem(e: unknown): HandoffFailure {
  if (!(e instanceof ApiError)) return 'server'
  if (e.status === 401 || e.status === 419) return 'link'
  if (e.status === 400 && /token is required/i.test(e.serverMessage ?? '')) return 'link'
  return 'server'
}

/**
 * The URL is minted by our own API, but it is still a string we hand straight
 * to a navigation. Pin it to http(s) so a malformed or tampered response can
 * never turn into a `javascript:` execution in the user's tab.
 */
function assertNavigable(url: string): void {
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    throw new HandoffError('server', 'The handoff service returned an unusable address.')
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    throw new HandoffError('server', 'The handoff service returned an unusable address.')
  }
}

const LINK_DEAD = 'This sign-in link has expired or has already been used.'

export interface HandoffApi {
  /**
   * GET /lc/users/handoff/start — requires the Ssid header.
   *
   * Returns the destination as JSON; it deliberately does NOT redirect. A
   * cross-origin 302 is followed automatically by fetch/XHR, which lands you on
   * a CORS-blocked request to the peer instead of on a navigation. Returning
   * the URL keeps the decision in the caller.
   *
   * Every call MINTS a ticket (Redis SETEX, 60s). Never call it to probe
   * whether the peer exists — only in response to the user asking to switch.
   */
  start: () => Promise<HandoffTarget>

  /**
   * POST /lc/users/handoff/redeem — deliberately NO Ssid header: this origin
   * has no session yet, which is the entire point.
   *
   * Single-use. Redis deletes the `jti → ssid` mapping on first read, whether
   * or not the rest of the request succeeded, so a retry ALWAYS fails and
   * always looks like a backend bug. Callers must fire this exactly once per
   * navigation and send the user to sign-in on any failure.
   */
  redeem: (token: string) => Promise<HandoffSession>
}

/**
 * @param client       authenticated client — carries the SSID header for `/start`.
 * @param anonClient   client for `/redeem`. Defaults to `client`, but the
 *   console passes a SEPARATE instance with no `onUnauthorized` hook: redeem's
 *   401s are the normal, expected way a spent link fails, and letting the
 *   shared hook fire on them wipes the session and hard-navigates to login
 *   before the route can say what happened.
 */
export function createHandoffApi(client: ApiClient, anonClient: ApiClient = client): HandoffApi {
  return {
    start: async () => {
      let res: z.infer<typeof handoffStartResponse>
      try {
        res = await client.request('GET', '/lc/users/handoff/start', {
          schema: handoffStartResponse,
        })
      } catch (e) {
        const reason = classifyStart(e)
        const message =
          reason === 'no-peer'
            ? 'No peer environment is configured for this deployment.'
            : reason === 'session'
              ? 'Your session is no longer valid. Sign in again.'
              : e instanceof Error
                ? e.message
                : 'The handoff service could not be reached.'
        throw new HandoffError(reason, message, e instanceof ApiError ? e.status : null)
      }
      assertNavigable(res.data.redirect_url)
      return { url: res.data.redirect_url }
    },

    redeem: async (token) => {
      // An empty token is the `400 token is required` case, and we already know
      // the answer. Spending a round trip to be told so only widens the 60s
      // window for the tickets that are real.
      if (!token) throw new HandoffError('link', LINK_DEAD, null)

      let res: z.infer<typeof handoffRedeemResponse>
      try {
        res = await anonClient.request('POST', '/lc/users/handoff/redeem', {
          body: { token },
          schema: handoffRedeemResponse,
          auth: false,
        })
      } catch (e) {
        const reason = classifyRedeem(e)
        const message =
          reason === 'link'
            ? LINK_DEAD
            : e instanceof Error
              ? e.message
              : 'The handoff service could not be reached.'
        throw new HandoffError(reason, message, e instanceof ApiError ? e.status : null)
      }

      // A 200 with no SSID is not a session. Treat it as a dead link rather
      // than handing `undefined` to the sign-in path.
      if (!res.data.SSID) throw new HandoffError('link', LINK_DEAD, 200)
      return { ssid: res.data.SSID }
    },
  }
}
